/* ================================================================
   BRITTEN CUSTOM: Your Britten, Messages (a bride's own conversations, and writing back)
   ================================================================
   Added:     2 Oct 2026
   Author:    Claude / Matthew
   Location:  assets/britten-yb-messages.js (+ .css), loaded by snippets/britten-yb-saved.liquid only,
              and only with settings.britten_your_britten_signin AND settings.britten_your_britten_messages on
   Spec:      docs/britten-core-CONTRACT.md sections 16, 17 and 21

   PURPOSE:
   The #messages tab on the Saved page: a row per conversation, one conversation at a time and, with flags.send,
   a reply box and a New message card. Unread markers, the studio line and compose({ order }) are step 3.

   HOW IT WORKS:
   Nothing is fetched on page load: the list when she clicks "Show my messages" or the tab, or arrives on
   #messages; a conversation when she opens it (the Support Hub, her access token; on step_up the shared code
   form asks once more). One conversation or the New message card replaces the list through data-yb-msg-open on
   #messages (css only). Text is written with textContent; nothing becomes a link. Unread = lastFrom 'britten'
   and lastAt newer than yb.seen[id] (the yb tool slot, 50 ids at most, seeded as read on a device's first
   load); yb.unread is the count the header panel reads. Sending: files up first, then one POST; a reply shows
   at once as "You, just now" and the conversation is fetched again 150 seconds later.

   CHANGELOG:
   - 2 Oct 2026: built; sending (flags.send); QA fixes (a long conversation opens at its newest message; 20-second limit).
   - 3 Oct 2026: redesign step 1 (section 19): the Messages tab keeps focus when the list arrives.
   - 3 Oct 2026: redesign step 3 (section 21): the conversation is a view (no accordion), unread markers and the tab dot,
     the studio status line, compose(), the New message layout, dates through BrittenYbPage.fmt.
   - 4 Oct 2026: QA fixes (section 22): UK time in the studio line, 12-hour times without the leading zero, New message keeps
     the page's scroll, a second tap on the tab and Escape in the header, focus after a sign-in in the card.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  var CFG = window.BrittenCoreConfig;
  if (!FEATURE_ENABLED || !CFG || !CFG.flags || !CFG.flags.yourBritten || !CFG.flags.signin || !CFG.flags.messages) return;
  if (!window.Promise || !window.fetch) return;
  var sec = document.querySelector('[data-yb-messages]');
  if (!sec || /[?&]s=/.test(window.location.search)) return;   // no section = a switch is off; ?s= is the read-only view

  var TOOL = 'your-britten';
  var SLOT = 'yb';   // the tool slot: seen (id -> the newest time she has seen) and unread (a count)
  var HUB = String(CFG.supportHubUrl || 'https://brittensupporthub.netlify.app').replace(/\/+$/, '');
  var CLAMP = 1200;   // characters: a longer message starts folded
  var LONGT = 4;   // messages: a conversation longer than this opens at its newest message
  var SEND = !!CFG.flags.send;   // sending is a second switch: with it off none of the code below the list runs
  var MAXT = 5000, WARN = 4500;   // characters in a message: the hard stop, and where the counter appears
  var MAXF = 10, MAXB = 20 * 1048576;   // files per message and bytes per file: the Support Hub form's own limits
  var LATER = 150000;   // ms: how long after a send before the Support Hub's copy is looked at once more
  var OKTYPE = /^(image\/(jpeg|png|webp|heic|heif)|application\/pdf)$/;

  var core = null;
  var Y = null;   // window.BrittenYbPage: fmt (dates), loc, mask, stepUp, timed
  var i18n = null;   // the name must end in i18n: scripts/i18n-js-keys-check.py finds call sites by it
  var T = {};   // the page's own words (Liquid spans, data-yb-mstr)
  // g: bumped on sign-out (an answer in flight is dropped). busy/tb: the list/a conversation is loading. th: opened
  // conversations (p: her replies not shown by the server yet). open: the open row or -1. sn: what she has seen.
  // q: a compose() waiting for the list. dr: drafts. nc: the New message card. fly: sends on the wire. tm: timers.
  var S = { g: 0, st: '', w: null, busy: false, tb: false, list: null, th: {}, open: -1, sn: {}, q: 0, dr: {}, nc: null, fly: 0, tm: [], stale: 0, u: 0 };
  var el = {};

  // === HELPERS ===
  function log(m) { if (DEBUG_MODE && window.console) console.log('[Britten YB messages]', m); }
  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function cl(e, s) { return e && e.closest ? e.closest(s) : null; }
  function h(tag, cls, txt) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (txt) e.textContent = txt;
    return e;
  }
  function btn(cls, txt, attr) {
    var b = h('button', cls, txt);
    b.type = 'button';
    b.setAttribute(attr, '');
    return b;
  }
  function emit(name, p) { p.tool = TOOL; core.events.emit(name, p); }
  function who() { var s = core.session.state(); return s.signedIn ? s.bride.email || '1' : ''; }
  function day(v) {   // Today, Yesterday, or the page's date rule ("2 October")
    var d = new Date(v), n = new Date(), k = Math.round((new Date(n.getFullYear(), n.getMonth(), n.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 864e5);
    return k === 0 ? i18n.t('js.yb.msg_today') : k === 1 ? i18n.t('js.yb.msg_yesterday') : Y.fmt(d);
  }
  function when(iso, time) {
    var d = new Date(iso || NaN), dt, tm;
    if (isNaN(d.getTime())) return '';
    dt = day(d);
    tm = d.toLocaleTimeString(Y.loc, { hour: new Intl.DateTimeFormat(Y.loc, { hour: 'numeric' }).resolvedOptions().hour12 ? 'numeric' : '2-digit', minute: '2-digit' });   // 2:11 PM, not 02:11 PM; a 24-hour clock keeps 09:05
    return time ? i18n.t('js.yb.msg_when', { date: dt, time: tm }) : dt;
  }
  function bytes(n) { return n < 1024 ? n + ' B' : n < 1048576 ? Math.round(n / 1024) + ' KB' : Math.round(n / 104857.6) / 10 + ' MB'; }
  function studio() {   // the status line in every [data-yb-studio]: the footer's rule (assets/britten-footer.js), Bath time, Mon to Fri 9am to 4pm, shut on its closed dates
    var p = {}, c = ($('[data-bf-closed]') || sec).getAttribute('data-bf-closed') || '', d, m, o, w, k = 1;
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
    d = Date.UTC(p.year, p.month - 1, p.day);
    m = (+p.hour % 24) * 60 + +p.minute;
    function on(i) { var x = new Date(d + i * 864e5); return !!(x.getUTCDay() % 6) && c.indexOf(x.toISOString().slice(0, 10)) < 0; }
    o = on(0) && m >= 540 && m < 960;
    if (o) { w = T.st_open; }
    else if (on(0) && m < 540) { w = T.st_today; }
    else {
      while (k < 15 && !on(k)) k++;
      w = T.st_next.replace('{when}', k === 1 ? T.st_tomorrow : T.st_on.replace('{day}', new Intl.DateTimeFormat(Y.loc, { weekday: 'long', timeZone: 'UTC' }).format(d + k * 864e5)));
    }
    $$('[data-yb-studio]').forEach(function (n) { n.setAttribute('data-yb-studio', o ? 'open' : 'closed'); n.children[1].textContent = w; });
  }
  function show(name) {   // one state at a time: guest, idle, loading, step, list, empty, slow, retry, new
    var v = name === 'new' ? 'n' : name === 'list' && S.open >= 0 ? 't' : '';   // a conversation or the card replaces the list (css)
    S.st = name;
    $$('[data-yb-msg-state]', sec).forEach(function (n) { n.hidden = n.getAttribute('data-yb-msg-state') !== name; });
    if (el.top) el.top.hidden = !/^(idle|list|empty|slow|retry)$/.test(name);   // "New message" waits for a signed-in section
    if (v) sec.setAttribute('data-yb-msg-open', v); else sec.removeAttribute('data-yb-msg-open');
    studio();
  }
  function home() { show(S.list ? (S.list.length ? 'list' : 'empty') : 'idle'); }   // where she was before the card
  function uuid() {
    var c = window.crypto;
    if (c && c.randomUUID) return c.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (k) { var r = Math.random() * 16 | 0; return (k === 'x' ? r : (r & 3 | 8)).toString(16); });
  }

  // === REQUESTS ===
  function post(body, path) {   // { j: the answer } or { r: the reason }; path: threads (the default), orders or message
    var tok = core.session.token();
    if (!tok) return Promise.resolve({ r: 'step_up' });   // no live token: a fresh code gives her one
    function go(sig) {
      return fetch(HUB + '/api/account/' + (path || 'threads'), { method: 'POST', signal: sig, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok }, body: JSON.stringify(body) }).then(function (r) {
        return r.json().then(null, function () { return {}; }).then(function (j) {
          j = j || {};
          return r.ok ? { j: j } : { r: j.reason || 'try_again' };
        });
      }, function () { return { r: 'try_again' }; });
    }
    return Y.timed(go, { r: 'try_again' });
  }
  // step_up or sign_in: a fresh code through the shared form, then ONE more try; "back" is the state to return to.
  function ask(body, back, path) {
    var g = S.g;
    function go(again) {
      return post(body, path).then(function (r) {
        if (r.j || again || g !== S.g || (r.r !== 'step_up' && r.r !== 'sign_in')) return r;
        show('step');
        return Y.stepUp(el.step, true).then(function (ok) {
          if (!ok || g !== S.g) return { r: 'idle' };
          show(back);
          return go(true);
        });
      });
    }
    return go(false);
  }

  // === THE LIST, AND WHAT SHE HAS SEEN ===
  function ts(t) { return Date.parse(t.lastAt) || 0; }
  function isNew(t) { return t.lastFrom === 'britten' && ts(t) > (S.sn[t.id] || 0); }
  function status(t) { return t.lastFrom === 'britten' ? i18n.t('js.yb.msg_replied') : i18n.t('js.yb.msg_with_us'); }
  function dot(n) {   // the Messages tab's gold dot, with its words for a screen reader
    var d = el.dot;
    if (!d) return;
    d.hidden = !n;
    d.textContent = '';
    if (n) d.appendChild(h('span', 'yb-sr', ' ' + (n === 1 ? T.unread_one : T.unread_other.replace('{n}', n))));
  }
  function tally() { var n = S.list.filter(isNew).length; core.store.set(SLOT, 'unread', n); dot(n); }   // yb.unread: the header panel reads it
  function track(l) {   // a list arrived: seed it as read on a device's first load, else keep what she has seen (at most 50 ids)
    var old = core.store.get(SLOT, 'seen', null), o = {};
    S.list = l.filter(function (t) { return t && t.id; }).slice(0, 50);
    S.list.forEach(function (t) { if (!old) o[t.id] = ts(t); else if (old[t.id]) o[t.id] = old[t.id]; });
    S.sn = o;
    core.store.set(SLOT, 'seen', o);
    S.th = {};
  }
  function seen(t, c) {   // she has opened it: the newest time she has seen
    S.sn[t.id] = Math.max.apply(0, c.messages.map(function (m) { return Date.parse(m.at) || 0; }).concat(ts(t)));
    core.store.set(SLOT, 'seen', S.sn);
    draw(true);
  }
  function row(t, i) {
    var li = h('li', 'yb-msg__item'), u = isNew(t), b = btn('yb-msg__row' + (u ? ' yb-msg__row--new' : ''), '', 'data-yb-msg-i'), x = h('span', 'yb-msg__tx'), m = h('span', 'yb-msg__meta'), d = h('span', 'yb-msg__dot'), n = Number(t.count) || 0;
    b.setAttribute('data-yb-msg-i', i);
    d.setAttribute('aria-hidden', 'true');
    b.appendChild(d);
    x.appendChild(h('span', 'yb-msg__subject', t.subject || i18n.t('js.yb.msg_untitled')));
    m.appendChild(h('span', 'yb-msg__date', when(t.lastAt)));
    m.appendChild(h('span', 'yb-msg__pill' + (u ? ' yb-msg__pill--new' : ''), u ? T.new_reply : status(t)));
    m.appendChild(h('span', 'yb-msg__n', i18n.n('js.yb.msg_count', n, { n: n })));
    x.appendChild(m);
    b.appendChild(x);
    li.appendChild(b);
    return li;
  }
  function draw(quiet) {   // quiet: a refresh she did not ask for says nothing aloud
    S.ul.textContent = '';
    S.list.forEach(function (t, i) { S.ul.appendChild(row(t, i)); });
    el.live.textContent = quiet ? '' : i18n.n('js.yb.msg_conversations', S.list.length, { n: S.list.length });
    tally();
  }
  function load(keep) {   // keep: a refresh after not_found, so the note stays
    var g = S.g;
    if (!who() || S.busy) return;
    S.busy = true;
    if (!keep) el.note.textContent = '';
    show('loading');
    ask({}, 'loading').then(function (r) {
      var l = r.j && Array.isArray(r.j.threads) ? r.j.threads : null, n;
      if (g !== S.g) return;
      S.busy = false;
      if (l) {
        S.open = -1;
        track(l);
        draw();
        n = S.list.length;
        show(n ? 'list' : 'empty');
        emit('messages_open', { count: n, surface: 'saved' });
      } else {
        show(r.r === 'slow_down' ? 'slow' : r.r === 'idle' ? 'idle' : 'retry');
      }
      if (S.q) { n = S.q; S.q = 0; compose(n); }   // an "Ask about this order" was waiting
      else if (!cl(document.activeElement, '[data-yb-nav]')) el.title.focus({ preventScroll: true });   // a tab she chose keeps focus
    }).then(null, function (e) { log(e); if (g === S.g) { S.busy = false; show('retry'); } });
  }

  // === ONE CONVERSATION ===
  function put(k, v) { $('[data-f="' + k + '"]', el.mv).textContent = v; }
  function msgs(box, c) {   // the messages and the side card, now and later; the reply box is never touched
    var ul = $('.yb-msg__msgs', box), att = false, n, a, z;
    var ms = c.messages.map(function (m, k) { return { m: m || {}, k: k, t: Date.parse((m || {}).at) || 0 }; }).sort(function (a, b) { return a.t - b.t || a.k - b.k; }).map(function (x) { return x.m; }).concat(c.p || []);   // oldest first, then her unconfirmed replies
    ul.textContent = '';
    ms.forEach(function (m) {
      var mine = m.from !== 'britten', li = h('li', 'yb-msg__m yb-msg__m--' + (mine ? 'y' : 'b')), by = h('p', 'yb-msg__who'), tx = String(m.text == null ? '' : m.text), p = h('p', 'yb-msg__text', tx), q;
      by.appendChild(h('span', 'yb-msg__by', mine ? i18n.t('js.yb.msg_you') : i18n.t('js.yb.msg_britten')));
      by.appendChild(document.createTextNode(' · ' + (m.now ? i18n.t('js.yb.msg_just_now') : when(m.at, true))));
      li.appendChild(by);
      li.appendChild(p);
      if (tx.length > CLAMP) {
        p.className += ' yb-msg__text--clamp';
        q = btn('yb-link', i18n.t('js.yb.msg_more'), 'data-yb-msg-more');
        q.setAttribute('aria-expanded', 'false');
        li.appendChild(q);
      }
      (Array.isArray(m.attachments) ? m.attachments : []).forEach(function (a) {
        var nm = String((a && a.name) || ''), n = a && a.size;
        att = true;
        li.appendChild(h('p', 'yb-msg__att', typeof n === 'number' && n >= 0 ? i18n.t('js.yb.msg_attachment', { name: nm, size: bytes(n) }) : i18n.t('js.yb.msg_attachment_plain', { name: nm })));
      });
      ul.appendChild(li);
    });
    n = ms.length;
    a = ms[0] || {};
    z = ms[n - 1] || {};
    $('.yb-msg__eyebrow', box).textContent = T.conv_eyebrow.replace('{n}', i18n.n('js.yb.msg_count', n, { n: n }));
    put('count', n);
    put('started', a.at ? day(a.at) : '');
    put('latest', z.now ? day(new Date()) : z.at ? day(z.at) : '');
    put('pill', z.from === 'britten' ? i18n.t('js.yb.msg_replied') : i18n.t('js.yb.msg_with_us'));
    $('[data-f="att"]', el.mv).hidden = !att;
  }
  function fill(t, c) {
    var box = el.mb, ul = h('ul', 'yb-msg__msgs'), hd = h('h3', 'yb-msg__h', t.subject || i18n.t('js.yb.msg_untitled'));
    hd.id = 'yb-msg-h-' + S.open;
    hd.tabIndex = -1;
    ul.setAttribute('aria-labelledby', hd.id);
    box.removeAttribute('aria-live');
    box.textContent = '';
    box.appendChild(h('p', 'yb-msg__eyebrow'));
    box.appendChild(hd);
    box.appendChild(ul);
    msgs(box, c);
    if (SEND) typed(composer(t.id, box)._c);
    seen(t, c);
    emit('message_thread_open', { count: c.messages.length, surface: 'saved' });
    hd.focus({ preventScroll: true });
    if (ul.children.length > LONGT) ul.lastChild.scrollIntoView({ block: 'start' });   // a long one opens at its newest message (css: scroll-margin-top)
  }
  function more(b) {
    var on = b.getAttribute('aria-expanded') !== 'true';
    b.previousSibling.className = on ? 'yb-msg__text' : 'yb-msg__text yb-msg__text--clamp';
    b.setAttribute('aria-expanded', on ? 'true' : 'false');
    b.textContent = on ? i18n.t('js.yb.msg_less') : i18n.t('js.yb.msg_more');
  }
  function close(back) {
    var i = S.open, b;
    if (i < 0) return;
    S.open = -1;
    el.mb.textContent = '';   // the reply box goes; the draft is kept
    show('list');
    b = S.ul.children[i];
    if (back && b) b.firstChild.focus();
    if (back && S.stale) { S.stale = 0; fresh(); }   // a refresh waited for her to leave the conversation
  }
  function note(box, text) {   // a calm line inside the conversation
    box.textContent = '';
    box.appendChild(h('p', 'yb-note', text));
  }
  function gone() {   // not_found: it is not hers any more; close it and fetch the list again
    close(false);
    el.note.textContent = i18n.t('js.yb.msg_gone');
    load(true);
  }
  function open(i) {
    var t = S.list[i], box = el.mb, g = S.g, c = S.th['_' + t.id];
    S.open = i;
    show('list');
    sec.scrollIntoView();   // the panel's scroll margin lands the page at the top
    box.textContent = '';
    if (c) { fill(t, c); return; }
    box.setAttribute('aria-live', 'polite');
    box.appendChild(h('p', 'yb-note', el.loading.textContent));
    S.tb = true;
    ask({ threadId: t.id }, 'list').then(function (r) {
      var c = r.j && r.j.thread && Array.isArray(r.j.thread.messages) ? r.j.thread : null;
      if (g !== S.g) return;
      S.tb = false;
      show('list');
      if (c) { c.p = []; c.n = c.messages.length; S.th['_' + t.id] = c; }
      if (S.open !== i) return;   // she closed it while it came
      if (c) {
        fill(t, c);
      } else if (r.r === 'not_found') {
        gone();
      } else if (r.r === 'idle') {
        close(true);   // she cancelled at the code
      } else {
        note(box, r.r === 'slow_down' ? i18n.t('js.yb.msg_slow') : i18n.t('js.yb.msg_retry'));
      }
    }).then(null, function (e) {
      log(e);
      if (g !== S.g) return;
      S.tb = false;
      if (S.st !== 'list') show('list');
      if (S.open === i) note(box, i18n.t('js.yb.msg_retry'));
    });
  }

  // === SENDING (flags.send) ===
  function say(C, t) { C.st.textContent = t || ''; }   // the composer's status line (role="status")
  function warn(e) { e.preventDefault(); e.returnValue = ''; return ''; }
  function fly(n) {   // a send is on the wire: the browser's own "leave this page?" prompt, for that long
    S.fly += n;
    window[S.fly > 0 ? 'addEventListener' : 'removeEventListener']('beforeunload', warn);
  }
  function later(fn) { S.tm.push(setTimeout(fn, LATER)); }
  function composer(id, host) {   // a reply box for conversation id, or the New message form and its side column, appended to host
    var fr = $('[data-yb-msg-tpl="' + (id ? 'reply' : 'new') + '"]', sec).content.cloneNode(true), f = $('form', fr), C = { f: f, id: id, k: id ? '_' + id : '', files: [], keys: null, sid: '' }, w = $('[data-yb-msg-who]', f), s = core.session.state();
    C.set = $('fieldset', f);
    C.ta = $('[data-yb-msg-text]', f);
    C.ct = $('[data-yb-msg-count]', f);
    C.ch = $('[data-yb-msg-chips]', f);
    C.st = $('[data-yb-msg-status]', f);
    C.ord = $('[data-yb-msg-order]', f);
    C.ob = $('.yb-msg__ordbox', f);
    C.ct.id = 'yb-msg-ct-' + (++S.u);
    C.ta.setAttribute('aria-describedby', C.ct.id);
    if (id) C.ta.value = S.dr[C.k] || '';
    if (w) w.textContent = [core.store.get(SLOT, 'firstName', '') || (s.bride && s.bride.firstName), Y.mask(who())].filter(Boolean).join(' · ');
    host.appendChild(fr);
    f._c = C;
    return f;
  }
  function typed(C) {   // after every change to the text: the hard stop, the height, the counter, the draft
    var v = C.ta.value, n;
    if (v.length > MAXT) C.ta.value = v = v.slice(0, MAXT);
    n = v.length;
    C.ta.style.height = 'auto';
    C.ta.style.height = C.ta.scrollHeight + C.ta.offsetHeight - C.ta.clientHeight + 'px';   // grows with the text; the css stops it at eight lines
    C.ct.hidden = n < WARN;
    C.ct.textContent = n < WARN ? '' : i18n.t('js.yb.msg_left', { left: (MAXT - n).toLocaleString(Y.loc) });
    say(C, n < MAXT ? '' : i18n.t('js.yb.msg_max'));
    if (C.id) { if (n) S.dr[C.k] = v; else delete S.dr[C.k]; }
  }
  function ftype(f) { var t = f.type || (/\.(heic|heif)$/i.test(f.name) ? 'image/heic' : ''); return OKTYPE.test(t) ? t : ''; }
  function chips(C) {   // one chip per chosen file, each with a button that takes it out
    C.ch.textContent = '';
    C.files.forEach(function (x, i) {
      var li = h('li', 'yb-msg__chip'), b = btn('yb-msg__x', '×', 'data-yb-msg-rm');
      b.setAttribute('data-yb-msg-rm', i);
      b.setAttribute('aria-label', i18n.t('js.yb.msg_remove', { name: x.f.name }));
      li.appendChild(h('span', 'yb-msg__chipn', x.f.name + ' · ' + bytes(x.f.size)));
      li.appendChild(b);
      C.ch.appendChild(li);
    });
  }
  function pick(C, inp) {   // files chosen: the ones that fit are kept, the rest are named calmly
    var l = Array.prototype.slice.call(inp.files || []), m = '';
    inp.value = '';
    l.forEach(function (f) {
      var t = ftype(f);
      if (!t) m = i18n.t('js.yb.msg_file_type', { name: f.name });
      else if (f.size > MAXB) m = i18n.t('js.yb.msg_big', { name: f.name, size: bytes(MAXB) });
      else if (C.files.length >= MAXF) m = i18n.t('js.yb.msg_files_max', { max: MAXF });
      else C.files.push({ f: f, t: t });
    });
    C.keys = null;   // a different set of files: upload again
    chips(C);
    say(C, m);
  }
  function orders(C, b, want) {   // "About an order?": her orders are fetched only now; trouble removes the row quietly. want: an order to have chosen
    var g = S.g, r0 = b.parentNode;
    b.disabled = true;
    post({}, 'orders').then(function (r) {
      var l = r.j && Array.isArray(r.j.orders) ? r.j.orders : [];
      if (g !== S.g) return;
      C.ord.length = 1;
      l.forEach(function (o) {
        var op;
        if (!o || !o.name) return;
        op = h('option', '', [o.name, Y.fmt(o.created_at), o.lines && o.lines[0] && o.lines[0].title].filter(Boolean).join(' · '));
        op.value = o.name;
        C.ord.appendChild(op);
      });
      if (want) {
        C.ord.value = want;
        if (C.ord.value !== want) { l = h('option', '', want); l.value = want; C.ord.appendChild(l); C.ord.value = want; }   // not in her list: the name still goes
      }
      r0.hidden = true;
      C.ob.hidden = C.ord.length < 2;
      if (C.ord.length > 1 && !want) C.ord.focus();
    }).then(null, function (e) { log(e); if (g === S.g) r0.hidden = true; });
  }
  function upload(C) {   // her files up: one init call for all of them, then a PUT each. Resolves to the keys
    var fs = C.files, k = [];
    if (!fs.length) return Promise.resolve(k);
    if (C.keys) return Promise.resolve(C.keys);   // a retry with the same files: they are already there
    return fetch(HUB + '/api/uploads/init', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ files: fs.map(function (x) { return { filename: x.f.name, contentType: x.t, size: x.f.size }; }), submissionId: C.sid }) }).then(function (r) {
      return r.ok ? r.json() : Promise.reject(r);
    }).then(function (j) {
      var u = j && j.uploads;
      if (!Array.isArray(u) || u.length !== fs.length) return Promise.reject(j);
      return u.reduce(function (p, x, i) {
        return p.then(function () {
          var hd = { 'Content-Type': fs[i].t };
          if (x.token) hd['x-upsert'] = 'true';
          return fetch(x.uploadUrl, { method: 'PUT', headers: hd, body: fs[i].f }).then(function (r) { if (!r.ok) return Promise.reject(r); k.push(x.key); });
        });
      }, Promise.resolve());
    }).then(function () { return (C.keys = k); });
  }
  function reset(C) {   // after a send: nothing of the message is left in the form
    C.ta.value = '';
    C.files = [];
    C.keys = null;
    C.sid = '';
    if (C.ord) C.ord.selectedIndex = 0;
    chips(C);
    typed(C);
  }
  function sent(C, txt, names) {
    var id = C.id, c = id && S.th['_' + id], box = cl(C.f, '[data-yb-mv-main]'), t = box && S.list[S.open];
    reset(C);
    emit('message_sent', { kind: id ? 'reply' : 'new', files: names.length });
    if (id) {
      if (c) {
        c.p.push({ from: 'you', now: 1, text: txt, attachments: names });   // shown at once; the 150-second look replaces it
        if (box) msgs(box, c);
      }
      if (t) { t.lastFrom = 'you'; t.lastAt = new Date().toISOString(); t.count = (Number(t.count) || 0) + 1; draw(true); }   // the row says "With us"
      say(C, i18n.t('js.yb.msg_sent_reply'));
      C.ta.focus();
      later(function () { again(id); });
    } else {
      el.note.textContent = i18n.t('js.yb.msg_sent_new');
      home();
      el.title.focus({ preventScroll: true });   // the card she was in is gone
      later(fresh);
    }
  }
  function send(C) {
    var txt = C.ta.value.trim(), g = S.g, id = C.id, to = id ? 'list' : 'new', names = C.files.map(function (x) { return { name: x.f.name }; });
    if (C.busy) return;
    if (!txt) { say(C, i18n.t('js.yb.msg_need')); C.ta.focus(); return; }
    C.busy = C.set.disabled = true;
    say(C, i18n.t('js.yb.msg_sending'));
    fly(1);
    C.sid = C.sid || uuid();   // one id per message: a retry of the same message carries the same one
    upload(C).then(function (keys) {
      var b = { text: txt, submissionId: C.sid };
      if (id) b.threadId = id;
      if (C.ord && C.ord.value) b.order = C.ord.value;
      if (keys.length) b.fileKeys = keys;
      return ask(b, to, 'message');
    }).then(null, function (e) { log(e); return { r: 'try_again' }; }).then(function (r) {
      fly(-1);
      if (g !== S.g) return;   // she signed out while it went: nothing of it is shown
      C.busy = C.set.disabled = false;
      if (S.st === 'step') show(to);   // she cancelled the code form
      if (r.j && r.j.ok) { sent(C, txt, names); return; }
      if (r.r === 'not_found' && id) { gone(); return; }
      say(C, r.r === 'slow_down' ? i18n.t('js.yb.msg_slow') : i18n.t('js.yb.msg_send_retry'));   // a cancelled code form lands here too
      C.ta.focus();   // her words are still in the box
    });
  }
  function again(id) {   // 150 seconds after a reply, once: what the Support Hub holds now (her reply stays until it shows there)
    var g = S.g;
    post({ threadId: id }).then(function (r) {
      var c = r.j && r.j.thread && Array.isArray(r.j.thread.messages) ? r.j.thread : null, o = S.th['_' + id], l = S.list && S.list[S.open];
      if (!c || !o || g !== S.g) return;
      c.n = c.messages.length;
      c.p = c.n < o.n + o.p.length ? o.p : [];
      S.th['_' + id] = c;
      if (l && l.id === id) msgs(el.mb, c);
    });
  }
  function fresh() {   // 150 seconds after a new message, once: the list again, quietly; never over an open conversation
    var g = S.g;
    if (!S.list || S.busy) return;
    if (S.open >= 0) { S.stale = 1; return; }
    post({}).then(function (r) {
      var l = r.j && Array.isArray(r.j.threads) ? r.j.threads : null;
      if (!l || g !== S.g) return;
      if (S.open >= 0) { S.stale = 1; return; }
      track(l);
      draw(true);
      if (S.st === 'list' || S.st === 'empty') home();
    });
  }
  function openNew(want) {   // the New message card; want: an order name to have chosen
    if (!S.nc) S.nc = composer(0, el.nbox)._c;
    show('new');
    sec.scrollIntoView();
    typed(S.nc);
    if (want) orders(S.nc, $('[data-yb-msg-ord]', S.nc.f), want);
    S.nc.ta.focus({ preventScroll: true });   // the page stays at the card's top, back control in sight
  }
  function compose(o) {   // window.BrittenYbMessages.compose({ order }): waits while the list is still coming
    if (!who()) return;
    if (S.busy) { S.q = o || {}; return; }
    openNew(o && o.order);
  }

  // === THE SECTION FOLLOWS THE SESSION ===
  function sync() {
    var w = who(), f = $('[data-yb-pref-step]'), c = f && !f.hidden && el.step.contains(f) && $('[data-yb-pref-step-cancel]', f);
    if (w === S.w) return;
    S.w = w;
    S.g++;   // whatever is still in flight is for somebody who has gone
    S.busy = S.tb = false;
    S.list = null;
    S.th = {};
    S.open = -1;
    S.sn = {};
    S.q = 0;
    S.tm.forEach(clearTimeout);   // the 150-second looks were for somebody who has gone
    S.tm = [];
    S.dr = {};
    S.stale = 0;
    if (S.nc) { S.nc = null; el.nbox.textContent = ''; }   // her unsent words and her orders go with her
    if (c) c.click();   // an open code form closes
    el.mb.textContent = S.ul.textContent = '';
    el.note.textContent = el.live.textContent = '';
    dot(w ? core.store.get(SLOT, 'unread', 0) : 0);
    show(w ? 'idle' : 'guest');
    if (w && S.f) $('[data-yb-msg-go]', sec).focus();   // she signed in inside this card (its inputs have lost focus by now)
  }
  function hash() {
    var a = $('[data-yb-saved]').getAttribute('data-yb-active');
    if (a && a !== 'messages') close(false);   // another tab: the conversation closes, focus stays with the tab
    if (window.location.hash === '#messages' && S.st === 'idle') load();
  }

  function init(c) {
    core = c;
    Y = window.BrittenYbPage;
    if (!Y || !Y.fmt) return;   // the page script lends the dates, the code form and the time limit
    i18n = { t: c.i18n.t, n: function (k, n, v) { var b = window.BrittenI18n; return b ? b.n(k, n, v) : ''; } };
    $$('[data-yb-mstr]', sec).forEach(function (n) { T[n.getAttribute('data-yb-mstr')] = n.textContent; });
    el.title = $('#yb-msg-title');
    el.note = $('[data-yb-msg-note]', sec);
    el.live = $('[data-yb-msg-live]', sec);
    el.step = $('[data-yb-msg-state="step"]', sec);
    el.loading = $('[data-yb-msg-state="loading"]', sec);
    el.dot = $('[data-yb-msg-unread]');
    el.mv = $('[data-yb-mv]', sec);
    el.mb = $('[data-yb-mv-main]', sec);
    S.ul = $('[data-yb-msg-list]', sec);
    if (SEND) {
      el.top = $('[data-yb-msg-top]', sec);
      el.nbox = $('[data-yb-msg-state="new"]', sec);
      sec.addEventListener('submit', function (e) {
        var f = cl(e.target, '[data-yb-msg-form]');
        if (!f) return;
        e.preventDefault();
        send(f._c);
      });
      sec.addEventListener('input', function (e) { var f = cl(e.target, '[data-yb-msg-form]'); if (f && e.target.hasAttribute('data-yb-msg-text')) typed(f._c); });
      sec.addEventListener('change', function (e) { var f = cl(e.target, '[data-yb-msg-form]'); if (f && e.target.hasAttribute('data-yb-msg-file')) pick(f._c, e.target); });
      window.BrittenYbMessages = { compose: compose };   // the order view's "Ask about this order"
    }
    sec.addEventListener('click', function (e) {
      var b = cl(e.target, 'button'), f = b && cl(b, '[data-yb-msg-form]'), C = f && f._c;
      if (!b) return;
      if (b.hasAttribute('data-yb-msg-go')) { load(); }
      else if (b.hasAttribute('data-yb-msg-i')) { if (!S.tb) open(Number(b.getAttribute('data-yb-msg-i'))); }
      else if (b.hasAttribute('data-yb-msg-back')) { close(true); }
      else if (b.hasAttribute('data-yb-msg-more')) { more(b); }
      else if (b.hasAttribute('data-yb-msg-new')) { openNew(); }
      else if (b.hasAttribute('data-yb-msg-cancel')) { home(); $('[data-yb-msg-new]', sec).focus(); }
      else if (b.hasAttribute('data-yb-msg-add')) { $('[data-yb-msg-file]', f).click(); }
      else if (b.hasAttribute('data-yb-msg-rm')) { C.files.splice(Number(b.getAttribute('data-yb-msg-rm')), 1); C.keys = null; chips(C); $('[data-yb-msg-add]', f).focus(); }
      else if (b.hasAttribute('data-yb-msg-ord')) { orders(C, b); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && S.open >= 0 && S.st === 'list' && !e.defaultPrevented && !cl(e.target, '[data-bh-header]')) close(true); });   // not the header panel's own Escape
    $('[data-yb-saved]').addEventListener('britten:yb:tab', function (e) {   // the chosen tab tapped again: the conversation or the New message card closes
      if (e.detail !== 'messages') return;
      if (S.open >= 0) close(false); else if (S.st === 'new') home();
    });
    $('[data-yb-nav-messages]').addEventListener('click', function () { if (S.st === 'idle') load(); });
    document.addEventListener('focusin', function (e) { S.f = sec.contains(e.target); });
    document.addEventListener('britten:session:change', sync);
    window.addEventListener('hashchange', hash);
    sync();
    sec.hidden = false;
    if (window.location.hash === '#messages') { sec.scrollIntoView(); hash(); }   // it was hidden when the browser looked for it
    log('ready');
  }
  function boot() {
    if (window.BrittenCore) window.BrittenCore.ready(function () { init(window.BrittenCore); });
  }
  if (window.BrittenCore) {
    boot();
  } else {
    document.addEventListener('britten:core:ready', function once() {
      document.removeEventListener('britten:core:ready', once);
      boot();
    });
  }
})();
